// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductDescriptionStyles } from "@vanmeter/Overrides/Components/ProductDescription";
import { fontConfig } from "@vanmeter/XDL/constants";
import { ellipsis, xScreen } from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

type SizeVariant = "large" | "medium" | "small";

interface SharedProductDescriptionStylesOptions {
    lines?: number;
    sm?: number;
    includeMinHeight?: boolean;
    sizeVariants?: SizeVariant;
}

export const sharedProductDescriptionStyles = ({
    lines = 1,
    sm = 2,
    includeMinHeight = false,
    sizeVariants = "large",
}: SharedProductDescriptionStylesOptions): ProductDescriptionStyles => {
    const smLines = sm ?? lines;
    const lineHeight = sizeVariants === "small" ? 18 : 24;
    const size = sizeVariants === "small" ? 16 : 20;
    const weight = sizeVariants === "large" ? fontConfig.weight.bold : fontConfig.weight.regular;

    const sharedTypography = {
        size,
        color: "text" as const,
        weight,
        fontFamily: fontConfig.secondaryFontFamily,
    };

    const ellipsisStyles = css`
        ${ellipsis(lines)}
        ${xScreen("lt", "sm")} {
            ${ellipsis(smLines)}
        }
    `;

    const minHeightStyles = css`
        min-height: ${includeMinHeight ? `${lines * lineHeight}px` : "unset"};
        ${xScreen("lt", "sm")} {
            min-height: ${includeMinHeight ? `${smLines * lineHeight}px` : "unset"};
        }
    `;

    return {
        productDetailDescription: {
            ...sharedTypography,
            css: css`
                display: block;
                letter-spacing: 0;
                line-height: ${lineHeight}px;
                ${ellipsisStyles}
                ${minHeightStyles}
                @media print {
                    display: block;
                    width: 100%;
                    text-align: start;
                }
            `,
        },
        productDetailLink: {
            color: "text",
            typographyProps: {
                ...sharedTypography,
                css: css`
                    line-height: ${lineHeight}px;
                    letter-spacing: 0;
                    ${ellipsisStyles}
                `,
            },
            css: css`
                align-items: flex-start;
                width: fit-content;
                ${xScreen("lt", "sm")} {
                    ${ellipsis(smLines)}
                }
                ${minHeightStyles}
                @media print {
                    display: inline-block;
                    width: 100%;
                    text-align: start;
                }
            `,
        },
    };
};
